import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import UserService from "../services/userService";
import useTheme from "../hooks/useTheme";
import logo from "../assets/logo.png";

export default function WelcomeLanding() {
  const { slug } = useParams();
  const { applyTheme } = useTheme();

  const [isLoading, setIsLoading] = useState(!!slug);
  const [hospitalName, setHospitalName] = useState("Misas");
  const [primaryColor, setPrimaryColor] = useState("#087F4C");

  useEffect(() => {
    if (slug) {
      setIsLoading(true);

      const fetchPromise = UserService.getHospitalBySlug(slug);

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Timeout")), 10000);
      });

      Promise.race([fetchPromise, timeoutPromise])
        .then((response) => {
          if (response?.success && response?.data) {
            const hospital = response.data;
            setHospitalName(hospital.name || "Misas");
            setPrimaryColor(hospital.primary_color || "#087F4C");

            // Save to localStorage
            localStorage.setItem("hospital_primary_color", JSON.stringify(hospital.primary_color));
            localStorage.setItem("hospital_secondary_color", JSON.stringify(hospital.secondary_color));
            localStorage.setItem("hospital_opaque_color", JSON.stringify(hospital.opaque_color));
            localStorage.setItem("hospital_name", JSON.stringify(hospital.name));
            localStorage.setItem("hospital_welcome_header", JSON.stringify(hospital.welcome_header));
            localStorage.setItem("hospital_welcome_writeup", JSON.stringify(hospital.welcome_writeup));
            localStorage.setItem("hospital_id", JSON.stringify(hospital.hospital_id));
            localStorage.setItem("hospital", JSON.stringify(hospital.hospital_id));

            // Apply theme instantly
            applyTheme({
              primary_color: hospital.primary_color,
              secondary_color: hospital.secondary_color,
              opaque_color: hospital.opaque_color,
            });

            // Set document title
            document.title = `${hospital.name} Patient Portal - Access Your Health Portal`;
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error loading hospital by slug:", err);
          
          // Clear any stored hospital branding from localStorage to use defaults
          const keysToRemove = [
            "hospital_primary_color",
            "hospital_secondary_color",
            "hospital_opaque_color",
            "hospital_name",
            "hospital_welcome_header",
            "hospital_welcome_writeup",
            "hospital_id",
            "hospital",
          ];
          keysToRemove.forEach((key) => localStorage.removeItem(key));
          applyTheme(null);
          setHospitalName("Misas");
          setPrimaryColor("#087F4C");
          document.title = "Misas Patient Portal - Download & Access Your Health Portal";
          setIsLoading(false);
        });
    } else {
      // Clear localStorage hospital branding if landing on root welcome without a slug
      const keysToRemove = [
        "hospital_primary_color",
        "hospital_secondary_color",
        "hospital_opaque_color",
        "hospital_name",
        "hospital_welcome_header",
        "hospital_welcome_writeup",
        "hospital_id",
        "hospital",
      ];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      applyTheme(null);
      setHospitalName("Misas");
      setPrimaryColor("#087F4C");
      document.title = "Misas Patient Portal - Download & Access Your Health Portal";
      setIsLoading(false);
    }
  }, [slug, applyTheme]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col justify-center items-center px-4 py-8">
        <div className="flex flex-col items-center space-y-6">
          {/* Pulsing Medical Cross / Spinner */}
          <div className="relative flex items-center justify-center">
            <div className="absolute w-24 h-24 rounded-full border-4 border-emerald-500/30 animate-ping"></div>
            <div className="w-16 h-16 rounded-full border-4 border-t-emerald-600 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute text-emerald-600 font-bold text-xl">
              +
            </div>
          </div>
          <div className="text-center">
            <h2 className="text-emerald-800 text-xl font-bold tracking-wide animate-pulse">
              Loading Landing Page...
            </h2>
            <p className="text-emerald-600 text-xs mt-1">
              Applying custom hospital styles
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hexColor = primaryColor.replace("#", "");
  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.misas.app";

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(playStoreUrl)}&color=${hexColor}`;

  return (
    <div className="bg-slate-50 text-slate-800 min-h-screen flex flex-col justify-between overflow-x-hidden gradient-bg">
      {/* Top Navigation */}
      <header className="w-full py-5 px-6 md:px-12 lg:px-24 flex items-center justify-between border-b border-slate-100 bg-white/60 backdrop-blur-md sticky top-0 z-50">
        <div className="flex items-center space-x-3">
          <img src={logo} alt="Misas Logo" className="h-10 w-10 object-contain hover:scale-105 transition-transform duration-300" />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            {hospitalName} <span className="text-primary100">Patient</span>
          </span>
        </div>
        <nav className="hidden md:flex items-center space-x-8">
          <a href="#features" className="text-sm font-medium text-slate-600 hover:text-primary100 transition-colors duration-200">
            Features
          </a>
          <a href="#download" className="text-sm font-medium text-slate-600 hover:text-primary100 transition-colors duration-200">
            Download
          </a>
          <Link to={slug ? `/${slug}` : "/"} className="text-sm font-semibold text-primary100 hover:text-primary100/80 transition-colors duration-200">
            Go to Web App →
          </Link>
        </nav>
        <div className="flex md:hidden">
          <Link to={slug ? `/${slug}` : "/"} className="px-4 py-2 text-xs font-semibold text-white bg-primary100 hover:bg-primary100/90 rounded-full shadow-md transition-all duration-200">
            Launch Portal
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="px-6 md:px-12 lg:px-24 pt-12 pb-20 max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Left Info Column */}
          <div className="flex-1 space-y-6 text-center lg:text-left animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full text-emerald-800 text-xs font-semibold">
              <svg className="w-4.5 h-4.5 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
              <span>Official {hospitalName} Patient Application</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Your Health, <br class="hidden lg:inline" />
              <span className="hero-gradient-text">Simplified.</span>
            </h1>
            <p className="text-slate-600 text-base md:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
              {hospitalName} connects you directly with your hospital records. Track your consultations, view lab results instantly, schedule appointments, and manage payments—all in one secure, intuitive app.
            </p>

            {/* Actions / Downloads */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-4">
              {/* Google Play store button */}
              <a href="https://play.google.com/store/apps/details?id=com.misas.app" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center bg-slate-900 hover:bg-slate-800 text-white px-6 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
                <svg className="w-6 h-6 mr-3 text-white group-hover:scale-105 transition-transform duration-200" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5,3.003L18.473,11.23a1.503,1.503,0,0,1,0,2.6L5,22.062a1.5,1.5,0,0,1-2.25-1.3V4.3a1.5,1.5,0,0,1,2.25-1.3Z" />
                </svg>
                <div className="text-left">
                  <p class="text-[10px] uppercase font-bold tracking-widest text-slate-400 leading-none">Get it on</p>
                  <p className="text-sm font-semibold mt-0.5 leading-none">Google Play</p>
                </div>
              </a>

              {/* Web app Launch button */}
              <Link to={slug ? `/${slug}` : "/"} className="w-full sm:w-auto inline-flex items-center justify-center bg-white hover:bg-slate-50 text-primary100 border-2 border-primary100 hover:border-primary100/90 px-6 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 font-bold text-sm">
                <svg className="w-5 h-5 mr-2 text-primary100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                </svg>
                Launch Web Portal
              </Link>
            </div>
          </div>

          {/* Right QR Column */}
          <div className="flex-1 w-full max-w-md animate-fade-in-up delay-200">
            <div className="glass-panel rounded-3xl p-8 text-center glow-effect shadow-xl relative overflow-hidden group hover:scale-[1.02] transition-all duration-500">
              <div className="absolute -right-20 -top-20 w-44 h-44 bg-primary100/5 rounded-full blur-2xl"></div>
              <div className="absolute -left-20 -bottom-20 w-44 h-44 bg-emerald-500/5 rounded-full blur-2xl"></div>

              {/* Inner Border Accent */}
              <div className="border border-slate-100 rounded-2xl p-6 bg-white/70">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Scan to Download</h3>
                <p className="text-slate-500 text-xs mb-6 max-w-xs mx-auto">
                  Point your device camera at this QR code to download the {hospitalName} app from the Google Play Store.
                </p>

                {/* Dynamic QR Code Container */}
                <div className="relative w-48 h-48 mx-auto flex items-center justify-center border-4 border-slate-50 bg-white rounded-2xl shadow-inner p-3 group-hover:scale-105 transition-transform duration-300">
                  <img src={qrCodeUrl} alt={`${hospitalName} App Download QR Code`} className="w-full h-full object-contain" id="qr_code_image" loading="lazy" />
                  <div className="absolute w-10 h-10 bg-white rounded-xl shadow-md flex items-center justify-center p-1.5 border border-slate-100">
                    <img src={logo} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                </div>

                {/* Device Badge Status Indicator */}
                <div className="mt-6 flex justify-center items-center space-x-3 text-xs font-semibold text-slate-600 bg-slate-50 py-2 px-4 rounded-xl">
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary100 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary100"></span>
                  </span>
                  <span>Supports Android, iOS & Desktop</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section id="features" className="bg-white border-t border-slate-100 py-20 px-6 md:px-12 lg:px-24">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center max-w-2xl mx-auto space-y-4">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                Everything You Need, in One Secure Portal
              </h2>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Say goodbye to waiting in queues and lost physical cards. {hospitalName} empowers you to take charge of your clinic visits and clinical records.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-6">
              {/* Feature 1: Patient Card */}
              <div className="p-6 border border-slate-100 hover:border-primary100/25 hover:shadow-lg rounded-2xl transition-all duration-300 space-y-4 bg-slate-50/50 group">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-primary100 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Digital Patient Card</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Access your official hospital identification card and biodata online. Never worry about misplacing your physical card.
                </p>
              </div>

              {/* Feature 2: Appointments */}
              <div className="p-6 border border-slate-100 hover:border-primary100/25 hover:shadow-lg rounded-2xl transition-all duration-300 space-y-4 bg-slate-50/50 group">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-primary100 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Seamless Appointments</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Schedule onsite visits or participate in secure, online tele-consultation video calls directly with your physician.
                </p>
              </div>

              {/* Feature 3: Queue Status */}
              <div className="p-6 border border-slate-100 hover:border-primary100/25 hover:shadow-lg rounded-2xl transition-all duration-300 space-y-4 bg-slate-50/50 group">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-primary100 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Real-Time Queue Status</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Check in via barcode and track your place in the clinic queue. Monitor waiting times remotely without being stuck in a lobby.
                </p>
              </div>

              {/* Feature 4: Records */}
              <div className="p-6 border border-slate-100 hover:border-primary100/25 hover:shadow-lg rounded-2xl transition-all duration-300 space-y-4 bg-slate-50/50 group">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-primary100 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Prescriptions & Lab Results</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Instantly check your prescriptions, lab test outcomes, and diagnostic summaries as soon as they are published by the doctor.
                </p>
              </div>

              {/* Feature 5: Billing & Receipts */}
              <div className="p-6 border border-slate-100 hover:border-primary100/25 hover:shadow-lg rounded-2xl transition-all duration-300 space-y-4 bg-slate-50/50 group">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-primary100 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Payments & Receipts</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Review your clinical bills, pay online securely via Paystack, and generate digital receipt slips for all past transactions.
                </p>
              </div>

              {/* Feature 6: Privacy & Security */}
              <div className="p-6 border border-slate-100 hover:border-primary100/25 hover:shadow-lg rounded-2xl transition-all duration-300 space-y-4 bg-slate-50/50 group">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center text-primary100 group-hover:scale-110 transition-transform duration-200">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Encrypted Privacy</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {hospitalName} uses enterprise-level encryption to safeguard your data. Your health records are confidential and completely secure.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Download Section */}
        <section id="download" className="px-6 md:px-12 lg:px-24 py-20 max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-emerald-800 to-primary100 text-white rounded-3xl p-8 md:p-16 text-center space-y-6 glow-effect relative overflow-hidden shadow-2xl">
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
            <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
            
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">Ready to Get Started?</h2>
            <p className="text-emerald-100 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
              Download the official Android app to scan codes, check-in, and manage appointments. Or launch the web application to view records instantly on any browser.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 relative z-10">
              <a href="https://play.google.com/store/apps/details?id=com.misas.app" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center bg-slate-900 hover:bg-slate-800 px-8 py-4 rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                <svg className="w-6 h-6 mr-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5,3.003L18.473,11.23a1.503,1.503,0,0,1,0,2.6L5,22.062a1.5,1.5,0,0,1-2.25-1.3V4.3a1.5,1.5,0,0,1-2.25-1.3Z" />
                </svg>
                <span className="font-bold text-sm text-left">
                  <span className="block text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Download For</span>
                  Android Device
                </span>
              </a>

              <Link to={slug ? `/${slug}` : "/"} className="w-full sm:w-auto inline-flex items-center justify-center bg-white hover:bg-slate-50 text-primary100 px-8 py-4 rounded-xl shadow-md transition-all duration-300 hover:-translate-y-0.5 font-bold text-sm">
                Launch Web Portal
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full py-8 px-6 md:px-12 lg:px-24 border-t border-slate-100 bg-white text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500 font-medium">
        <p>&copy; {new Date().getFullYear()} {hospitalName}. All rights reserved.</p>
        <div className="flex items-center space-x-6">
          <Link to={slug ? `/${slug}` : "/"} className="hover:text-primary100 transition-colors duration-200">
            Patient Portal
          </Link>
          <a href="https://play.google.com/store/apps/details?id=com.misas.app" target="_blank" rel="noopener noreferrer" className="hover:text-primary100 transition-colors duration-200">
            Play Store
          </a>
          <a href="/account-deletion" className="hover:text-primary100 transition-colors duration-200">
            Account Deletion
          </a>
        </div>
      </footer>
    </div>
  );
}
